import { createSearchFunctionFromCategory } from 'src/utils/categoryUtils';
import { ExcelGridConfig, ExcelUploadContext } from 'src/utils/excel/excelGridConfig';
import { showWarning } from 'src/utils/toast/toastUtil';
import { MST3040_R01DetailDto, MST3040_R01HeaderDto } from './Dto_MST3040_R01';
import { headerNames_MST3040_R01Detail } from './Util_MST3040_R01';

export const MST3040_R01_EXCEL_COLUMN_ORDER = [
	'coordinateCd', 'coordinateNm', 'coordinateSeq', 'xcoordinate', 'ycoordinate',
	'orient', 'topBottom', 'childItemcd', 'coordinateUsage',
	'afeederCd', 'bfeederCd', 'cfeederCd', 'remark',
];

const FEEDER_SEARCH_TARGETS = [
	{ field: 'afeederCd', type: 'afeeder', label: 'a피더코드' },
	{ field: 'bfeederCd', type: 'bfeeder', label: 'b피더코드' },
	{ field: 'cfeederCd', type: 'cfeeder', label: 'c피더코드' },
] as const;

const validateChildItemcd = async (
	newRow: MST3040_R01DetailDto,
	childItemcd: string,
	selectedHeader: MST3040_R01HeaderDto
) => {
	const searchFn = createSearchFunctionFromCategory('bom_childItemcd', {
		prntItemcd: selectedHeader.prntItemcd || '',
	});
	if (!searchFn) {
		throw new Error('엑셀에 적힌 좌표 중 모품목의 자품목코드를 찾을 수 없습니다. : ' + childItemcd);
	}

	const found = await searchFn(
		childItemcd.toUpperCase(),
		(item: { childItemcd?: string; itemNm?: string; itemSpec?: string; locationCd?: string; locationNm?: string }) => {
			newRow.childItemcd = item.childItemcd || childItemcd;
			newRow.childItemnm = item.itemNm || '';
			newRow.itemSpec = item.itemSpec || '';
			newRow.locationCd = item.locationCd || '';
			newRow.locationNm = item.locationNm || '';
		}
	);

	if (!found) {
		throw new Error('엑셀에 적힌 좌표 중 모품목의 자품목코드를 찾을 수 없습니다. : ' + childItemcd);
	}
};

const validateFeederCodes = async (newRow: MST3040_R01DetailDto) => {
	for (const target of FEEDER_SEARCH_TARGETS) {
		const feederCode = String(newRow[target.field] ?? '').trim();
		if (!feederCode) continue;

		const searchFn = createSearchFunctionFromCategory(target.type);
		if (!searchFn) {
			throw new Error(`엑셀에 적힌 좌표 중 ${target.label}를 찾을 수 없습니다. : ${feederCode}`);
		}

		const found = await searchFn(
			feederCode.toUpperCase(),
			(item: { minorCd?: string }) => {
				newRow[target.field] = item.minorCd || feederCode;
			}
		);

		if (!found) {
			throw new Error(`엑셀에 적힌 좌표 중 ${target.label}를 찾을 수 없습니다. : ${feederCode}`);
		}
	}
};

export const createMST3040ExcelConfig = (
	selectedHeader: MST3040_R01HeaderDto,
	defaultRow: MST3040_R01DetailDto,
	existingList: MST3040_R01DetailDto[]
): ExcelGridConfig<MST3040_R01DetailDto> => {
	const existingCoordinateCdSet = new Set(
		existingList
			.map((item) => String(item.coordinateCd ?? '').trim())
			.filter(Boolean)
	);

	return {
		headerNames: headerNames_MST3040_R01Detail,
		columnOrder: MST3040_R01_EXCEL_COLUMN_ORDER,
		defaultRow,
		template: {
			sheetName: '좌표 템플릿',
			fileNamePrefix: '좌표_업로드_템플릿',
			colWidths: [12, 18, 10, 10, 10, 10, 10, 15, 10, 12, 12, 12, 20],
		},
		onUploadError: (error) => {
			if (error instanceof Error && error.message.startsWith('엑셀')) {
				showWarning(error.message);
				return;
			}
			alert('엑셀 업로드 중 오류가 발생했습니다.');
		},
		processRow: async (row, index, _context: ExcelUploadContext) => {
			const newRow = { ...row };
			newRow.prntItemcd = selectedHeader.prntItemcd || '';

			const coordinateCd = String(newRow.coordinateCd ?? '').trim();
			if (coordinateCd && existingCoordinateCdSet.has(coordinateCd)) {
				throw new Error(`엑셀 ${index + 2}행의 좌표코드가 기존 데이터와 중복됩니다. : ${coordinateCd}`);
			}
			if (coordinateCd) {
				existingCoordinateCdSet.add(coordinateCd);
			}

			const childItemcd = String(newRow.childItemcd ?? '').trim();
			if (childItemcd) {
				await validateChildItemcd(newRow, childItemcd, selectedHeader);
			}

			await validateFeederCodes(newRow);

			return newRow;
		},
		createRowMeta: (_row, _index, valueIndex) => ({
			valueIndex,
			inserted: true,
			isModified: true,
		}),
	};
};
